import { useId, useState } from 'react';
import { useChangePassword } from '@/application/usecases/useSession';
import { useConfirmAction } from '@/features/shell/useConfirmAction';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import { Modal, modalStyles } from '@/ui/primitives/Modal';
import { toast } from '@/ui/primitives/toastStore';
import { PasswordInput } from '@/ui/primitives/PasswordInput';

/**
 * Changing your own password. Section 4 of the request of 13 September 2026.
 *
 * Opened from the key on the user block, on every screen for every role, and
 * from your own record. Three fields, with the autocomplete hints that make a
 * password manager behave. The only check made here is that the two new
 * passwords agree, since the server has no opinion about that; everything else
 * is the server's 400, rendered against the field it names. On success the
 * dialog closes with a toast. It does not sign anybody out: the session making
 * the change stays, and the server ends every other one.
 *
 * Two new passwords that agree are asked about before they are sent, for the
 * roles that are asked: the request of 5 October 2026 (third), section 2. The
 * question lies over the form, so Cancel comes back to it as it was typed. The
 * change the app demands at a first sign in is never asked about: there is
 * nothing to decide.
 */
export function ChangePasswordDialog({
  onClose,
  forced = null,
}: {
  onClose: () => void;
  /**
   * Set when the change is owed: signed in on the shared password. The dialog
   * cannot be dismissed, and the way out instead of Cancel is signing out.
   */
  forced?: { onSignOut: () => void } | null;
}) {
  const change = useChangePassword();
  const ask = useConfirmAction();
  const ids = { current: useId(), next: useId(), confirm: useId() };
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  function submit() {
    if (confirm !== next) {
      setErrors({ confirm: [strings.password.mismatch] });
      return;
    }
    setErrors({});

    const send = () =>
      change.mutateAsync({ currentPassword: current, newPassword: next }).then(
        () => {
          toast(strings.password.changed);
          onClose();
        },
        (error: Error) => {
          // The server names the field; the message goes under it, verbatim.
          const fields =
            'fieldErrors' in error
              ? (error as { fieldErrors: Record<string, string[]> }).fieldErrors
              : {};
          setErrors(fields);
          if (Object.keys(fields).length === 0) toast(failureWords(error));
        },
      );

    if (forced !== null) {
      void send();
      return;
    }
    ask(strings.confirm.ownPassword, send, {
      title: strings.password.title,
      confirmLabel: strings.password.submit,
    });
  }

  const busy = change.isPending;

  return (
    <Modal
      title={forced === null ? strings.password.title : strings.password.forcedTitle}
      lead={forced === null ? strings.password.lead : strings.password.forcedLead}
      onClose={forced === null ? onClose : () => undefined}
      footer={
        <>
          {forced === null ? (
            <Button onClick={onClose} disabled={busy}>
              {strings.password.cancel}
            </Button>
          ) : (
            <Button onClick={forced.onSignOut} disabled={busy}>
              {strings.password.signOut}
            </Button>
          )}
          <Button variant="primary" onClick={submit} disabled={busy}>
            {busy ? strings.password.changing : strings.password.submit}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
        noValidate
      >
        <div className={modalStyles.field}>
          <label className={modalStyles.label} htmlFor={ids.current}>
            {strings.password.current}
          </label>
          <PasswordInput
            id={ids.current}
            className={modalStyles.input}
            value={current}
            autoComplete="current-password"
            onChange={(event) => setCurrent(event.target.value)}
          />
          {errors.currentPassword !== undefined && (
            <p className={modalStyles.error} role="alert">
              {errors.currentPassword.join(' ')}
            </p>
          )}
        </div>

        <div className={modalStyles.field}>
          <label className={modalStyles.label} htmlFor={ids.next}>
            {strings.password.next}
          </label>
          <PasswordInput
            id={ids.next}
            className={modalStyles.input}
            value={next}
            autoComplete="new-password"
            onChange={(event) => setNext(event.target.value)}
          />
          {errors.newPassword !== undefined && (
            <p className={modalStyles.error} role="alert">
              {errors.newPassword.join(' ')}
            </p>
          )}
        </div>

        <div className={modalStyles.field}>
          <label className={modalStyles.label} htmlFor={ids.confirm}>
            {strings.password.confirm}
          </label>
          <PasswordInput
            id={ids.confirm}
            className={modalStyles.input}
            value={confirm}
            autoComplete="new-password"
            onChange={(event) => setConfirm(event.target.value)}
          />
          {errors.confirm !== undefined && (
            <p className={modalStyles.error} role="alert">
              {errors.confirm.join(' ')}
            </p>
          )}
        </div>

        {/* Enter submits, as on every other form; the footer buttons are outside the form. */}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  );
}
